<template>
  <!-- Modal Camera Fullscreen -->
  <div v-if="modelValue" ref="cameraModalRef" class="camera-modal camera-modal--fs">
    <div class="camera-box camera-box--fs">
      <!-- Top bar -->
      <div class="camera-header camera-header--fs">
        <div class="d-flex align-items-center text-white">
          <b>Chụp ảnh để {{ title }}</b>
          <span class="small text-white-50 ml-2" v-if="deviceLabel">({{ deviceLabel }})</span>
        </div>
        <div class="d-flex align-items-center">
          <button
            class="btn btn-sm btn-outline-light mr-2"
            @click="switchCamera"
            :disabled="loading || step !== 'live'"
          >
            <i class="fa fa-retweet"></i> Đổi camera
          </button>
          <button class="btn btn-sm btn-outline-light" @click="handleClose">
            Đóng
          </button>
        </div>
      </div>

      <!-- LIVE -->
      <div v-if="step === 'live'">
        <video
          ref="videoRef"
          autoplay
          playsinline
          muted
          class="camera-video camera-video--fs"
        ></video>

        <!-- Bottom bar (live) -->
        <div class="camera-actions camera-actions--fs">
          <button
            class="btn btn-success btn-lg btn-cta btn-cta-fs w-100"
            :disabled="busy || loading"
            @click="capture"
          >
            <i class="fa fa-camera mr-1"></i> Chụp
          </button>

          <div class="small text-white-50 mt-2 text-center" v-if="!geo.ready">
            Đang lấy vị trí…
          </div>
          <div
            class="small text-white-50 mt-1 text-center"
            v-else-if="geo.lat != null"
          >
            {{ geo.address || (geo.lat.toFixed(6) + ", " + geo.lng.toFixed(6)) }}
          </div>
        </div>
      </div>

      <!-- CONFIRM -->
      <div v-else class="confirm-pane">
        <img
          :src="previewUrl"
          class="confirm-img confirm-img--fs"
          alt="preview"
        />

        <!-- Bottom bar (confirm) -->
        <div class="camera-actions camera-actions--fs">
          <div class="d-flex w-100">
            <button
              class="btn btn-outline-light btn-lg btn-cta btn-cta-fs w-50 mr-2"
              :disabled="busy || loading"
              @click="retake"
            >
              <i class="fa fa-refresh mr-1"></i> Chụp lại
            </button>
            <button
              class="btn btn-success btn-lg btn-cta btn-cta-fs w-50"
              :disabled="busy || loading || !blob"
              @click="confirm"
            >
              <i class="fa fa-send mr-1"></i> Gửi
            </button>
          </div>
        </div>
      </div>

      <canvas ref="canvasRef" class="d-none"></canvas>
    </div>
  </div>
</template>

<script>
const DEFAULT_WM_LOGO = "/assets/img/profiles/logominhtien.png";
const DEFAULT_GEOAPIFY_KEY = "2209ae8943f74a18b5d0d7676d65cd50";

export default {
  name: "FullscreenCamera",
  props: {
    modelValue: { type: Boolean, required: true }, // v-model
    title: { type: String, default: "chấm công" }, // "chấm vào" | "chấm ra"...
    busy: { type: Boolean, default: false }, // trạng thái gửi/chấm công ở cha
    initialFacing: {
      type: String,
      default: "user", // 'user' | 'environment'
    },
    watermarkLogoUrl: { type: String, default: DEFAULT_WM_LOGO },
    geoapifyKey: { type: String, default: DEFAULT_GEOAPIFY_KEY },
  },
  data() {
    return {
      loading: false,
      // camera
      stream: null,
      devices: [],
      deviceIndex: 0,
      deviceLabel: "",
      lastFacing:
        this.initialFacing === "environment" ? "environment" : "user",
      // flow
      step: "live", // 'live' | 'confirm'
      previewUrl: null,
      blob: null,
      // geo
      geo: { ready: false, lat: null, lng: null, address: "" },
      // watermark logo cache
      _wmLogo: null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      async handler(val) {
        if (val) {
          await this.openCameraInternal();
        } else {
          this.stopCamera();
        }
      },
    },
  },
  mounted() {
    // preload logo
    const img = new Image();
    img.onload = () => (this._wmLogo = img);
    img.src = this.watermarkLogoUrl;
  },
  beforeUnmount() {
    this.stopCamera();
  },
  methods: {
    /* ===== OPEN/CLOSE ===== */
    async openCameraInternal() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Thiếu API camera");
        }
        this.loading = true;
        this.deviceLabel = "";
        this.step = "live";
        this.previewUrl = null;
        this.blob = null;
        this.geo = { ready: false, lat: null, lng: null, address: "" };

        const preferFacing =
          this.initialFacing === "environment" ? "environment" : "user";
        this.lastFacing = preferFacing;

        // start stream + devices
        await this.startStreamByFacing(preferFacing);
        await this.initDevices(preferFacing);

        // geolocation (không block UI)
        this.prepareGeo();

        this.$nextTick(() => this.enterFullscreen());
      } catch (err) {
        console.error(err);
        alert(
          "Không mở được camera. Vui lòng cấp quyền camera (HTTPS) và thử lại."
        );
        this.stopCamera();
        this.$emit("update:modelValue", false);
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.$emit("update:modelValue", false);
    },

    stopCamera() {
      this.exitFullscreen();
      this.stopCameraStreamOnly();
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.blob = null;
      this.step = "live";
      this.devices = [];
      this.deviceIndex = 0;
      this.deviceLabel = "";
      this.geo = { ready: false, lat: null, lng: null, address: "" };
    },
    stopCameraStreamOnly() {
      try {
        if (this.stream) {
          this.stream.getTracks().forEach((t) => t.stop());
        }
      } catch (_) {}
      this.stream = null;
    },

    /* ===== DEVICES / STREAM ===== */
    async initDevices(preferFacing = "user") {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const videos = devs.filter((d) => d.kind === "videoinput");
        this.devices = videos;

        let idx = 0;
        if (videos.length > 1) {
          const backIdx = videos.findIndex((d) =>
            /back|rear|environment/i.test(d.label)
          );
          const frontIdx = videos.findIndex((d) =>
            /front|user|face|webcam/i.test(d.label)
          );
          if (preferFacing === "environment" && backIdx >= 0) idx = backIdx;
          else if (preferFacing === "user" && frontIdx >= 0) idx = frontIdx;
        }
        this.deviceIndex = idx;
        this.deviceLabel = videos[idx]?.label || "";
      } catch (e) {
        console.error(e);
        this.devices = [];
        this.deviceLabel = "";
      }
    },
    async startStreamByFacing(facing = "user") {
      await this.startStream({
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
    },
    async startStreamByDeviceId(deviceId) {
      await this.startStream({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
    },
    async startStream(constraints) {
      this.stopCameraStreamOnly();
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.stream = stream;
      this.$nextTick(async () => {
        const video = this.$refs.videoRef;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {});
        }
      });
    },
    async switchCamera() {
      if (this.loading || this.step !== "live") return;
      try {
        this.loading = true;
        const vs = this.devices;
        if (vs.length > 1) {
          this.deviceIndex = (this.deviceIndex + 1) % vs.length;
          const dev = vs[this.deviceIndex];
          this.deviceLabel = dev?.label || "";
          await this.startStreamByDeviceId(dev.deviceId);
        } else {
          this.lastFacing =
            this.lastFacing === "user" ? "environment" : "user";
          await this.startStreamByFacing(this.lastFacing);
        }
      } finally {
        this.loading = false;
      }
    },

    /* ===== FULLSCREEN ===== */
    enterFullscreen() {
      try {
        const el = this.$refs.cameraModalRef;
        if (el && el.requestFullscreen) {
          el.requestFullscreen().catch(() => {});
        }
      } catch (_) {}
    },
    exitFullscreen() {
      try {
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
      } catch (_) {}
    },

    /* ===== CAPTURE / CONFIRM ===== */
    async capture() {
      if (this.loading || this.busy) return;
      try {
        const video = this.$refs.videoRef;
        const canvas = this.$refs.canvasRef;
        if (!video || !canvas) throw new Error("Thiếu phần tử camera");

        // chờ video có kích thước
        for (
          let i = 0;
          i < 10 && (!video.videoWidth || !video.videoHeight);
          i++
        ) {
          await new Promise((r) => setTimeout(r, 120));
        }

        const w = video.videoWidth || 1920;
        const h = video.videoHeight || 1080;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, w, h);

        const when = new Date();
        const { lat, lng, address } = await this.getGeoReady();
        await this.drawWatermarkLikeSample(ctx, w, h, {
          logo: this._wmLogo,
          when,
          address:
            address ||
            (lat != null && lng != null
              ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
              : ""),
        });

        const blob = await new Promise((res) =>
          canvas.toBlob(res, "image/jpeg", 0.92)
        );
        if (!blob) throw new Error("Không chụp được ảnh");

        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.blob = blob;
        this.previewUrl = URL.createObjectURL(blob);
        this.step = "confirm";
      } catch (e) {
        console.error(e);
        alert(e?.message || "Chụp thất bại, vui lòng thử lại.");
      }
    },
    retake() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.blob = null;
      this.step = "live";
    },
    async confirm() {
      if (!this.blob || this.busy) return;
      const { lat, lng, address } = await this.getGeoReady();
      const when = new Date();
      this.$emit("confirm", {
        blob: this.blob,
        lat,
        lng,
        address,
        createdAt: when,
      });
      // đóng hay không là do component cha quyết định (v-model)
    },

    /* ===== Watermark helpers ===== */
    async drawWatermarkLikeSample(ctx, w, h, opts = {}) {
      const logo = opts.logo || null;
      const when = opts.when instanceof Date ? opts.when : new Date();
      const addr = (opts.address || "").toString();

      const base = Math.max(w, h);
      const pad = Math.max(12, Math.round(base * 0.012));
      const timeFontPx = Math.max(44, Math.round(base * 0.05));
      const dateFontPx = Math.max(18, Math.round(base * 0.02));
      const addrFontPx = Math.max(14, Math.round(base * 0.017));
      const logoSize = Math.max(64, Math.round(base * 0.08));

      const extraAddr = addr ? addrFontPx + Math.round(pad * 0.2) : 0;
      const textTotalH = timeFontPx + dateFontPx + dateFontPx + extraAddr;
      const groupH = Math.max(logoSize, textTotalH);

      const top = h - pad - groupH;
      const left = pad;

      if (logo && (logo.complete || logo.decode)) {
        try {
          if (logo.decode) await logo.decode();
        } catch (_) {}
        const lx = left;
        const ly = top + Math.round((groupH - logoSize) / 2);
        ctx.save();
        this._roundedRect(ctx, lx, ly, logoSize, logoSize, 10);
        ctx.clip();
        ctx.drawImage(logo, lx, ly, logoSize, logoSize);
        ctx.restore();
      }

      const tx = left + logoSize + pad;
      let ty = top;

      const hh = String(when.getHours()).padStart(2, "0");
      const mm = String(when.getMinutes()).padStart(2, "0");
      const vn = this._formatVnDate(when);

      ctx.save();
      ctx.fillStyle = "#fff";
      ctx.textBaseline = "top";
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = Math.max(6, Math.round(base * 0.012));
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = Math.max(2, Math.round(base * 0.003));

      ctx.font = `700 ${timeFontPx}px sans-serif`;
      ctx.fillText(`${hh}:${mm}`, tx, ty);
      ty += timeFontPx + Math.round(pad * 0.2);

      ctx.font = `600 ${dateFontPx}px sans-serif`;
      ctx.fillText(vn.dateLine, tx, ty);
      ty += dateFontPx + Math.round(pad * 0.1);

      ctx.font = `500 ${dateFontPx}px sans-serif`;
      ctx.fillText(vn.weekday, tx, ty);
      ty += dateFontPx + Math.round(pad * 0.1);

      if (addr) {
        ctx.font = `400 ${addrFontPx}px sans-serif`;
        ctx.fillText(addr, tx, ty);
      }
      ctx.restore();
    },
    _roundedRect(ctx, x, y, w, h, r) {
      const rr = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.arcTo(x + w, y, x + w, y + h, rr);
      ctx.arcTo(x + w, y + h, x, y + h, rr);
      ctx.arcTo(x, y + h, x, y, rr);
      ctx.arcTo(x, y, x + w, y, rr);
      ctx.closePath();
    },
    _formatVnDate(d) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      const weekdays = [
        "Chủ Nhật",
        "Thứ Hai",
        "Thứ Ba",
        "Thứ Tư",
        "Thứ Năm",
        "Thứ Sáu",
        "Thứ Bảy",
      ];
      return {
        dateLine: `${day} Tháng ${month}, ${year}`,
        weekday: weekdays[d.getDay()],
      };
    },

    /* ===== GEO ===== */
    prepareGeo() {
      this.geo = { ready: false, lat: null, lng: null, address: "" };
      if (!navigator.geolocation) {
        this.geo.ready = true;
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.geo.lat = lat;
          this.geo.lng = lng;
          try {
            this.geo.address = await this.reverseGeocode(lat, lng);
          } catch (_e) {
            this.geo.address = "";
          }
          this.geo.ready = true;
        },
        () => {
          this.geo.ready = true;
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
      );
    },
    async getGeoReady() {
      if (!this.geo.ready) {
        await new Promise((r) => setTimeout(r, 3000));
      }
      return {
        lat: this.geo.lat,
        lng: this.geo.lng,
        address: this.geo.address,
      };
    },
    async reverseGeocode(lat, lng) {
      try {
        if (lat == null || lng == null) return null;
        const u = `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&lang=vi&apiKey=${encodeURIComponent(
          this.geoapifyKey
        )}`;
        const res = await fetch(u);
        const json = await res.json();
        const f = json?.features?.[0];
        return (
          f?.properties?.formatted ||
          `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        );
      } catch (_) {
        return lat && lng ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : null;
      }
    },
  },
};
</script>

<style scoped>
/* ==== Fullscreen camera ==== */
.camera-modal--fs {
  position: fixed;
  inset: 0;
  background: #000;
  z-index: 1050;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.camera-box--fs {
  position: relative;
  width: 100vw;
  height: 100vh;
  padding: 0;
  border-radius: 0;
  background: #000;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
}

/* base */
.camera-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.camera-box {
  width: min(96vw, 860px);
  background: #fff;
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
}
.camera-video--fs,
.confirm-img--fs {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.camera-header--fs {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  padding-top: calc(env(safe-area-inset-top) + 8px);
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0));
  color: #fff;
  z-index: 2;
}

.camera-actions--fs {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px calc(env(safe-area-inset-bottom) + 16px);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0));
  z-index: 2;
}

.btn-cta-fs {
  height: 56px;
  font-size: 18px;
  border-radius: 14px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
}

.confirm-pane {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.confirm-img {
  border: none;
}

@media (max-width: 576px) {
  .btn-cta-fs {
    height: 60px;
    font-size: 17px;
  }
}
</style>
